import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PhoneCall, CalendarCheck, Flame, Radio, ArrowUpRight, TrendingUp, Trash2, Loader2, Clock, Phone, Calendar } from "lucide-react";
import DispositionTag from "../components/DispositionTag";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const Kpi = ({ label, value, hint, icon: Icon, testid }) => (
  <div
    data-testid={testid}
    className="bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-[#FFB000]/30 transition-colors group"
  >
    <div className="flex items-start justify-between mb-5">
      <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40">
        {label}
      </div>
      <Icon className="w-4 h-4 text-white/30 group-hover:text-[#FFB000] transition-colors" strokeWidth={1.75} />
    </div>
    <div className="font-mono text-3xl font-medium text-white tracking-tighter">{value}</div>
    {hint && <div className="text-xs text-white/40 mt-2">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (call) => {
    if (!window.confirm("Delete this call record? This removes the call, consent submission, linked appointment, and feedback. Cannot be undone.")) {
      return;
    }
    setDeletingId(call.id);
    // Optimistic UI: drop the row immediately so the dashboard feels snappy.
    setStats((prev) => prev && {
      ...prev,
      recent_calls: (prev.recent_calls || []).filter((c) => c.id !== call.id),
    });
    try {
      const { data } = await api.delete(`/calls/${call.id}`);
      const d = data?.deleted || {};
      const parts = [];
      if (d.call) parts.push(`${d.call} call`);
      if (d.consent) parts.push(`${d.consent} consent`);
      if (d.appointment) parts.push(`${d.appointment} appt`);
      if (d.feedback) parts.push(`${d.feedback} feedback`);
      toast.success(parts.length ? `Removed: ${parts.join(", ")}` : "Call removed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete call");
      reload();
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        if (mounted) setStats(data);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-start justify-between mb-10">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
            Overview
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-white">
            Dashboard
          </h1>
          <p className="text-sm text-white/50 mt-2">
            Live view of Ashley's outbound activity.
          </p>
        </div>
        <Link
          data-testid="dashboard-new-campaign-btn"
          to="/campaigns/new"
          className="bg-[#FFB000] hover:bg-[#FFC133] text-black font-medium text-sm px-4 py-2 rounded-md transition-colors shadow-[0_0_18px_rgba(255,176,0,0.15)] flex items-center gap-2"
        >
          New Campaign <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm" data-testid="dashboard-loading">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <Kpi label="Calls Today" value={stats?.calls_today ?? 0} icon={PhoneCall} testid="kpi-calls-today" />
            <Kpi label="Calls This Week" value={stats?.calls_week ?? 0} icon={TrendingUp} testid="kpi-calls-week" />
            <Kpi label="Booked Today" value={stats?.booked_today ?? 0} icon={CalendarCheck} testid="kpi-booked-today" />
            <Kpi label="Hot Leads Open" value={stats?.hot_leads_open ?? 0} icon={Flame} testid="kpi-hot-leads" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
            <Kpi label="Active Campaigns" value={stats?.active_campaigns ?? 0} icon={Radio} testid="kpi-active-campaigns" />
            <Kpi label="Total Campaigns" value={stats?.total_campaigns ?? 0} icon={Radio} testid="kpi-total-campaigns" />
            <Kpi label="Calls This Month" value={stats?.calls_month ?? 0} icon={PhoneCall} testid="kpi-calls-month" />
          </div>

          {/* ── Today's View — every confirmed appointment for today, in order ── */}
          <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden mb-10" data-testid="todays-view">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-3.5 h-3.5 text-[#FFB000]" strokeWidth={2} />
                <div className="text-sm font-medium text-white">Today</div>
                <span className="text-[11px] font-mono text-white/40">
                  {(stats?.todays_appointments || []).length} {(stats?.todays_appointments || []).length === 1 ? "appointment" : "appointments"}
                </span>
              </div>
              <Link to="/appointments" className="text-xs text-[#FFB000] hover:underline" data-testid="view-all-appointments-link">
                View all →
              </Link>
            </div>
            <div className="divide-y divide-white/5">
              {(stats?.todays_appointments || []).length === 0 ? (
                <div className="px-5 py-10 text-center text-white/40 text-sm" data-testid="todays-view-empty">
                  Nothing booked for today.
                </div>
              ) : (
                (stats?.todays_appointments || []).map((a) => {
                  const local = a.scheduled_at ? new Date(a.scheduled_at) : null;
                  const timeStr = local ? local.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) : "—";
                  return (
                    <div
                      key={a.id}
                      data-testid={`todays-appt-${a.id}`}
                      className="px-5 py-3.5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex-shrink-0 w-20 text-[#FFB000] font-mono text-sm font-medium tabular-nums">
                        {timeStr}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white/90 truncate">
                          {a.name || "Unknown"}
                        </div>
                        <div className="text-[11px] text-white/40 mt-0.5 font-mono truncate">
                          {a.phone || a.email || "—"}
                          {a.notes ? <span className="text-white/55"> · {a.notes.slice(0, 80)}{a.notes.length > 80 ? "…" : ""}</span> : null}
                        </div>
                      </div>
                      {a.source && (
                        <div className="hidden sm:block text-[10px] uppercase font-mono tracking-wider text-white/30 w-16 text-right">
                          {a.source}
                        </div>
                      )}
                      {a.phone && (
                        <a
                          href={`tel:${a.phone}`}
                          data-testid={`todays-appt-call-${a.id}`}
                          title="Call"
                          className="flex-shrink-0 p-2 rounded-md text-white/40 hover:text-[#FFB000] hover:bg-[#FFB000]/10 transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5" strokeWidth={2} />
                        </a>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#FFB000] animate-pulse" />
                <div className="text-sm font-medium text-white">Recent Activity</div>
              </div>
              <Link to="/calls" className="text-xs text-[#FFB000] hover:underline" data-testid="view-all-calls-link">
                View all →
              </Link>
            </div>
            <div className="divide-y divide-white/5" data-testid="recent-activity-feed">
              {(stats?.recent_calls || []).length === 0 && (
                <div className="px-5 py-10 text-center text-white/40 text-sm">
                  No calls yet. Launch your first campaign to get started.
                </div>
              )}
              {(stats?.recent_calls || []).map((c) => (
                <div
                  key={c.id}
                  data-testid={`activity-row-${c.id}`}
                  className="group/row px-5 py-3.5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white/90 truncate">
                      {c.prospect_name || "Unknown"}{" "}
                      <span className="font-mono text-white/40 text-xs ml-2">
                        {c.prospect_phone}
                      </span>
                    </div>
                    <div className="text-[11px] text-white/40 mt-0.5 font-mono">
                      {c.created_at &&
                        formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                    </div>
                  </div>
                  <DispositionTag value={c.disposition || c.ai_disposition} />
                  <div className="text-xs font-mono text-white/40 w-16 text-right">
                    {c.duration_seconds ? `${c.duration_seconds}s` : c.status}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(c)}
                    disabled={deletingId === c.id}
                    title="Delete this call"
                    data-testid={`activity-delete-${c.id}`}
                    className="flex-shrink-0 p-1.5 rounded-md text-white/30 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover/row:opacity-100 transition-all disabled:opacity-50"
                  >
                    {deletingId === c.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
